/**
 * CustomerStatsCard - 고객 상담 통계 카드
 *
 * 고객의 상담 통계 정보(총 상담 수, 총 통화시간, 첫/최근 상담일)를 표시
 */

import { Phone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from './utils';

export interface CustomerStats {
  totalCallCount: number;
  totalCallDurationSeconds: number;
  firstCallDate: string | null;
  lastCallDate: string | null;
  visitCount?: number;
}

export interface CustomerStatsCardProps {
  stats: CustomerStats | null | undefined;
  isLoading?: boolean;
  /**
   * 카드 없이 내용만 표시
   */
  noCard?: boolean;
}

export function CustomerStatsCard({ stats, isLoading = false, noCard = false }: CustomerStatsCardProps) {
  const content = (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
        <Phone className="w-4 h-4" />
        상담 통계
      </h4>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold text-primary">{stats.totalCallCount}</p>
              <p className="text-xs text-muted-foreground">총 상담</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold text-primary">
                {stats.totalCallDurationSeconds
                  ? Math.round(stats.totalCallDurationSeconds / 60)
                  : 0}분
              </p>
              <p className="text-xs text-muted-foreground">총 통화시간</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="text-center p-3 bg-success/10 rounded-lg col-span-2">
              <p className="text-2xl font-bold text-success">{stats.visitCount || 0}</p>
              <p className="text-xs text-muted-foreground">총 방문 횟수</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <p className="text-xs text-muted-foreground">첫 상담일</p>
              <p className="text-sm">{formatDate(stats.firstCallDate)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">최근 상담일</p>
              <p className="text-sm">{formatDate(stats.lastCallDate)}</p>
            </div>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">
          통계 정보가 없습니다
        </p>
      )}
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card>
      <CardContent className="p-4">
        {content}
      </CardContent>
    </Card>
  );
}
